import type { InUseKind } from "~/gql/graphqlBase.generated";

import { RecordKind } from "~/gql/graphqlBase.generated";
import formatLocaleNumber from "~/helpers/localeNumberFormat";
import i18n from "~/i18n/i18n";

export interface InUseHolder {
	count: number;
	kind: InUseKind;
}

const MAX_NAMED_HOLDERS = 4;
const NAMED_WHEN_TRUNCATED = 3;

const kindKey = (kind: string): string => `common:usedBy.inUseKinds.${kind}`;

const hasLabel = (kind: string): boolean => i18n.exists(kindKey(kind), { count: 1 });

const sortLargestFirst = (holders: readonly InUseHolder[]): InUseHolder[] =>
	holders
		.map((holder, index) => ({ holder, index }))
		.filter(({ holder }) => holder.count > 0)
		.sort((a, b) => b.holder.count - a.holder.count || a.index - b.index)
		.map(({ holder }) => holder);

const labelHolder = ({ count, kind }: InUseHolder): string =>
	i18n.t(kindKey(kind), { count, qty: formatLocaleNumber(count) });

const describeHolders = (breakdown: null | readonly InUseHolder[] | undefined): null | string => {
	const sorted = sortLargestFirst(breakdown ?? []);
	if (!sorted.some((holder) => hasLabel(holder.kind))) return null;
	const allLabeled = sorted.every((holder) => hasLabel(holder.kind));
	const named =
		sorted.length <= MAX_NAMED_HOLDERS && allLabeled
			? sorted
			: sorted.filter((holder) => hasLabel(holder.kind)).slice(0, NAMED_WHEN_TRUNCATED);
	const rest = sorted.filter((holder) => !named.includes(holder));
	const parts = named.map(labelHolder);
	if (rest.length > 0) {
		const records = rest.reduce((sum, holder) => sum + holder.count, 0);
		parts.push(
			i18n.t("common:usedBy.inUseMoreRecords", {
				count: records,
				qty: formatLocaleNumber(records),
				types: i18n.t("common:usedBy.inUseTypes", { count: rest.length, qty: formatLocaleNumber(rest.length) })
			})
		);
	}
	return i18n.t("common:usedBy.inUseHolders", { holders: parts });
};

const formatInUseSentence = (
	kind: RecordKind,
	name: string,
	breakdown: null | readonly InUseHolder[] | undefined
): null | string => {
	const holders = describeHolders(breakdown);
	if (!holders) return null;
	const subject = i18n.t(`common:usedBy.subjects.${kind}`);
	const sentence = i18n.t("common:usedBy.refusedDeleteInUse", { holders, name, subject });
	return kind === RecordKind.CustomerType
		? i18n.t("common:usedBy.refusedDeleteInUseCustomerType", { sentence })
		: sentence;
};

export default formatInUseSentence;
